/**
 * Операции над чертежом, сохраняющие его планарность.
 */
import { dist, projectOnSegment, segmentIntersection, type Pt } from "@/shared/lib/geometry";
import { mapFixtures } from "./layers";
import {
	wallFaceKey,
	type PlanLayers,
	type Wall,
	type WallFinish,
	type WallGraph,
} from "./types-def";
import { WallMaterial } from "@/shared/catalog";
import { uid } from "./ids";
import { clampOffset } from "./walls";

export interface AddWallOptions {
	thickness: number;
	height: number;
	material: WallMaterial;
	bearing?: boolean;
	mounted?: boolean;
	nodeTolerance: number;
	edgeTolerance: number;
}

export const WALL_EDGE_TOLERANCE = 40;
export const MIN_WALL_LENGTH = 50;

export type WallConflictKind = "cross" | "overlap";

export interface WallConflict {
	kind: WallConflictKind;
	wallId: string;
}

export interface AddWallResult {
	layers: PlanLayers;
	endNodeId: string | null;
	conflict?: WallConflict;
}

export const splitWallAt = (
	layers: PlanLayers,
	wallId: string,
	point: Pt,
): { layers: PlanLayers; nodeId: string } => {
	const plan = layers.walls;
	const wall = plan.walls[wallId];
	const a = plan.nodes[wall.a];
	const b = plan.nodes[wall.b];

	const nodeId = uid("n");
	const nodes = { ...plan.nodes, [nodeId]: { id: nodeId, x: point.x, y: point.y } };

	const walls = { ...plan.walls };
	delete walls[wallId];
	const shared = {
		thickness: wall.thickness,
		height: wall.height,
		material: wall.material,
		bearing: wall.bearing,
		demolished: wall.demolished,
		mounted: wall.mounted,
	};
	const first: Wall = { id: uid("w"), a: wall.a, b: nodeId, ...shared };
	const second: Wall = { id: uid("w"), a: nodeId, b: wall.b, ...shared };
	walls[first.id] = first;
	walls[second.id] = second;

	const splitOffset = dist(a, point);
	const secondLength = dist(point, b);
	const openings = { ...plan.openings };
	for (const opening of Object.values(plan.openings)) {
		if (opening.wallId !== wallId) continue;
		if (opening.offset <= splitOffset) {
			openings[opening.id] = {
				...opening,
				wallId: first.id,
				offset: clampOffset(opening.offset, opening.width, splitOffset),
			};
		} else {
			openings[opening.id] = {
				...opening,
				wallId: second.id,
				offset: clampOffset(opening.offset - splitOffset, opening.width, secondLength),
			};
		}
	}

	const withItems = mapFixtures(layers, (fixture) => {
		if (fixture.wallId !== wallId) return fixture;
		return fixture.offset <= splitOffset
			? { ...fixture, wallId: first.id, offset: Math.min(fixture.offset, splitOffset) }
			: {
					...fixture,
					wallId: second.id,
					offset: Math.min(fixture.offset - splitOffset, secondLength),
				};
	});

	// Отделка разрезанной стены остаётся на обеих половинках: направление у них то же,
	// поэтому и стороны те же
	const finishes: Record<string, WallFinish> = {};
	for (const finish of Object.values(layers.finishes?.items ?? {})) {
		if (finish.wallId !== wallId) {
			finishes[finish.id] = finish;
			continue;
		}
		for (const half of [first.id, second.id]) {
			const id = wallFaceKey(half, finish.side);
			finishes[id] = { ...finish, id, wallId: half };
		}
	}

	return {
		layers: {
			...withItems,
			walls: { ...plan, nodes, walls, openings },
			...(layers.finishes ? { finishes: { ...layers.finishes, items: finishes } } : {}),
		},
		nodeId,
	};
};

function findNodeAt(plan: WallGraph, point: Pt, tolerance: number): string | null {
	let best: string | null = null;
	let bestDistance = tolerance;
	for (const node of Object.values(plan.nodes)) {
		const d = dist(node, point);
		if (d <= bestDistance) {
			bestDistance = d;
			best = node.id;
		}
	}
	return best;
}

interface Anchor {
	point: Pt;
	nodeId: string | null;
	wallId: string | null;
}

type AnchorOptions = Pick<AddWallOptions, "nodeTolerance" | "edgeTolerance">;

function locate(
	plan: WallGraph,
	point: Pt,
	options: AnchorOptions,
	ignoreDemolished = false,
): Anchor {
	const existing = findNodeAt(plan, point, options.nodeTolerance);
	if (existing) {
		const node = plan.nodes[existing];
		return { point: { x: node.x, y: node.y }, nodeId: existing, wallId: null };
	}

	for (const wall of Object.values(plan.walls)) {
		if (ignoreDemolished && wall.demolished) continue;
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b) continue;
		const projection = projectOnSegment(point, a, b);
		if (projection.distance > options.edgeTolerance) continue;
		if (dist(projection.point, a) < MIN_WALL_LENGTH || dist(projection.point, b) < MIN_WALL_LENGTH)
			continue;
		return { point: projection.point, nodeId: null, wallId: wall.id };
	}

	return { point, nodeId: null, wallId: null };
}

function resolveNode(
	layers: PlanLayers,
	point: Pt,
	options: AnchorOptions,
	ignoreDemolished = false,
): { layers: PlanLayers; nodeId: string } {
	const plan = layers.walls;
	const anchor = locate(plan, point, options, ignoreDemolished);
	if (anchor.nodeId) return { layers, nodeId: anchor.nodeId };
	if (anchor.wallId) return splitWallAt(layers, anchor.wallId, anchor.point);

	const nodeId = uid("n");
	return {
		layers: {
			...layers,
			walls: {
				...plan,
				nodes: { ...plan.nodes, [nodeId]: { id: nodeId, x: point.x, y: point.y } },
			},
		},
		nodeId,
	};
}

const PARALLEL_SIN = 1e-3;
const CONTACT_SLACK = 1;

export const findWallConflict = (
	plan: WallGraph,
	from: Pt,
	to: Pt,
	options: AnchorOptions & Pick<AddWallOptions, "thickness"> & { ignoreDemolished?: boolean },
): WallConflict | null => {
	const start = locate(plan, from, options, options.ignoreDemolished);
	const end = locate(plan, to, options, options.ignoreDemolished);
	const a = start.point;
	const b = end.point;
	const length = dist(a, b);
	if (length < MIN_WALL_LENGTH) return null;
	if (start.nodeId && start.nodeId === end.nodeId) return null;

	const dir = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
	const touches = (wall: Wall) =>
		wall.id === start.wallId ||
		wall.id === end.wallId ||
		[wall.a, wall.b].some((id) => id === start.nodeId || id === end.nodeId);

	for (const wall of Object.values(plan.walls)) {
		if (options.ignoreDemolished && wall.demolished) continue;
		const p = plan.nodes[wall.a];
		const q = plan.nodes[wall.b];
		if (!p || !q) continue;
		const wallLength = dist(p, q);
		if (wallLength < 1e-6) continue;
		const wallDir = { x: (q.x - p.x) / wallLength, y: (q.y - p.y) / wallLength };
		const sin = Math.abs(dir.x * wallDir.y - dir.y * wallDir.x);

		if (sin < PARALLEL_SIN) {
			const gap = Math.abs((p.x - a.x) * -dir.y + (p.y - a.y) * dir.x);
			if (gap >= (options.thickness + wall.thickness) / 2 - CONTACT_SLACK) continue;
			const s0 = (p.x - a.x) * dir.x + (p.y - a.y) * dir.y;
			const s1 = (q.x - a.x) * dir.x + (q.y - a.y) * dir.y;
			const shared = Math.min(Math.max(s0, s1), length) - Math.max(Math.min(s0, s1), 0);
			if (shared > CONTACT_SLACK) return { kind: "overlap", wallId: wall.id };
			continue;
		}

		if (touches(wall)) continue;
		const hit = segmentIntersection(a, b, p, q);
		if (!hit) continue;
		const tEps = CONTACT_SLACK / length;
		const uEps = CONTACT_SLACK / wallLength;
		if (hit.t <= tEps || hit.t >= 1 - tEps) continue;
		if (hit.u <= uEps || hit.u >= 1 - uEps) continue;
		return { kind: "cross", wallId: wall.id };
	}

	return null;
};

const wallExists = (plan: WallGraph, a: string, b: string, ignoreDemolished = false) =>
	Object.values(plan.walls).some(
		(w) =>
			(!ignoreDemolished || !w.demolished) &&
			((w.a === a && w.b === b) || (w.a === b && w.b === a)),
	);

export const addWallSegment = (
	layers: PlanLayers,
	from: Pt,
	to: Pt,
	options: AddWallOptions,
): AddWallResult => {
	if (dist(from, to) < MIN_WALL_LENGTH) return { layers, endNodeId: null };

	const ignoreDemolished = Boolean(options.mounted);
	const conflict = findWallConflict(layers.walls, from, to, {
		...options,
		ignoreDemolished,
	});
	if (conflict) return { layers, endNodeId: null, conflict };

	let current = layers;
	const start = resolveNode(current, from, options, ignoreDemolished);
	current = start.layers;
	const end = resolveNode(current, to, options, ignoreDemolished);
	current = end.layers;
	if (start.nodeId === end.nodeId) return { layers, endNodeId: start.nodeId };

	const work = current.walls;
	const a = work.nodes[start.nodeId];
	const b = work.nodes[end.nodeId];
	const length = dist(a, b);
	const epsilon = Math.min(0.02, 20 / Math.max(1, length));

	const cuts: { t: number; nodeId: string }[] = [];
	for (const node of Object.values(work.nodes)) {
		if (node.id === start.nodeId || node.id === end.nodeId) continue;
		const projection = projectOnSegment(node, a, b);
		if (projection.distance > options.edgeTolerance) continue;
		if (projection.t <= epsilon || projection.t >= 1 - epsilon) continue;
		cuts.push({ t: projection.t, nodeId: node.id });
	}

	const ordered = [
		{ t: 0, nodeId: start.nodeId },
		...cuts.toSorted((x, y) => x.t - y.t),
		{ t: 1, nodeId: end.nodeId },
	];

	const walls = { ...work.walls };
	for (let i = 0; i < ordered.length - 1; i++) {
		const fromId = ordered[i].nodeId;
		const toId = ordered[i + 1].nodeId;
		if (fromId === toId) continue;
		if (wallExists({ ...work, walls }, fromId, toId, ignoreDemolished)) continue;
		const id = uid("w");
		walls[id] = {
			id,
			a: fromId,
			b: toId,
			thickness: options.thickness,
			height: options.height,
			material: options.material,
			bearing: options.bearing,
			...(options.mounted ? { mounted: true } : {}),
		};
	}

	return { layers: { ...current, walls: { ...work, walls } }, endNodeId: end.nodeId };
};

/**
 * Автоматический пересчёт согласованности демонтажа и монтажа при изменении исходного плана.
 * Если стена исходного плана (`!wall.mounted && !wall.demolished`) после правок совпала
 * или наложилась на возводимую перегородку (`wall.mounted`), дублирующая перегородка монтажа
 * автоматически убирается, так как стена уже есть в исходном плане.
 */
export const reconcilePlanStages = (plan: WallGraph): WallGraph => {
	const mountedWalls = Object.values(plan.walls).filter((w) => w.mounted);
	if (mountedWalls.length === 0) return plan;

	const standingInitialWalls = Object.values(plan.walls).filter((w) => !w.mounted && !w.demolished);
	if (standingInitialWalls.length === 0) return plan;

	const redundantIds = new Set<string>();
	for (const mounted of mountedWalls) {
		const ma = plan.nodes[mounted.a];
		const mb = plan.nodes[mounted.b];
		if (!ma || !mb) continue;
		const mLen = dist(ma, mb);
		if (mLen < 1e-6) continue;
		const mDir = { x: (mb.x - ma.x) / mLen, y: (mb.y - ma.y) / mLen };

		for (const init of standingInitialWalls) {
			if (
				(init.a === mounted.a && init.b === mounted.b) ||
				(init.a === mounted.b && init.b === mounted.a)
			) {
				redundantIds.add(mounted.id);
				break;
			}
			const ia = plan.nodes[init.a];
			const ib = plan.nodes[init.b];
			if (!ia || !ib) continue;
			const iLen = dist(ia, ib);
			if (iLen < 1e-6) continue;
			const iDir = { x: (ib.x - ia.x) / iLen, y: (ib.y - ia.y) / iLen };
			const sin = Math.abs(mDir.x * iDir.y - mDir.y * iDir.x);
			if (sin < PARALLEL_SIN) {
				const gap = Math.abs((ia.x - ma.x) * -mDir.y + (ia.y - ma.y) * mDir.x);
				if (gap < (mounted.thickness + init.thickness) / 2 - CONTACT_SLACK) {
					const s0 = (ia.x - ma.x) * mDir.x + (ia.y - ma.y) * mDir.y;
					const s1 = (ib.x - ma.x) * mDir.x + (ib.y - ma.y) * mDir.y;
					const shared = Math.min(Math.max(s0, s1), mLen) - Math.max(Math.min(s0, s1), 0);
					if (shared > MIN_WALL_LENGTH) {
						redundantIds.add(mounted.id);
						break;
					}
				}
			}
		}
	}

	if (redundantIds.size === 0) return plan;
	const walls = { ...plan.walls };
	const openings = { ...plan.openings };
	for (const id of redundantIds) {
		delete walls[id];
	}
	for (const o of Object.values(openings)) {
		if (redundantIds.has(o.wallId)) delete openings[o.id];
	}
	return { ...plan, walls, openings };
};
